import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../api/client";
import { ApiException, type ApiList } from "../../api/types";

/**
 * «Кто играет» в учебной игре (13.10, владелец: «в учебных играх, при включении этого флага, надо добавить
 * возможность указать кто из сотрудников и принятых на работу будет играть»). Игроки — сотрудники, НЕ назначения:
 * ни зарплаты, ни грейдов, ни отчёта игры; отчёты друг о друге запрашивает ОО в расстановке (peer_review).
 *  - кандидаты — GET /api/bookings/learning-staff, лениво: только когда «Учеб» включён;
 *  - текущий состав брони (edit) — GET /api/bookings/learning?booking_id=;
 *  - 403 у кандидатов (оператор) — ряд скрыт: состав выбирают ЕО/ОО/ДК, операторский сервер всё равно отбросит.
 * payload — undefined, пока состав не загружен: ранний «Сохранить» не затрёт серверный набор
 * (нет поля learning_players — сервер состав не трогает).
 */
export interface LearningStaff {
  id: string; name: string; nickname: string | null; roles: string[];
  has_tg: boolean; hired_at: string | null;
  /** Принят на работу ≤30 дней назад. */
  is_new: boolean;
  /** Есть TG и роль оператора — напишет отчёт друг о друге из кабинета оператора. */
  can_report: boolean;
  /** Уволен, но уже стоит в составе этой игры (в кандидатах его нет — подмешан из брони). */
  gone?: boolean;
}

interface RawPlayer { booking_id: number; staff_id: string; name: string | null; date: string }

type Phase = "idle" | "ready" | "denied" | "error";

/** Потолок сервера (MAX_LEARNING_PLAYERS, api bookings/learning.ts): ловим до «Сохранить» — сервер отбил бы
 *  его уже ПОСЛЕ записи самой брони (ошибка на экране, а бронь сохранена без состава). */
export const LEARNING_MAX = 20;

export function useLearningPlayers(bookingId: number | undefined, isLearning: boolean, onPick?: (n: number) => void) {
  const [phase, setPhase] = useState<Phase>("idle");
  const [staff, setStaff] = useState<LearningStaff[]>([]);
  const [current, setCurrent] = useState<{ id: string; name: string }[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const pickRef = useRef(onPick);
  pickRef.current = onPick;

  /* Грузим один раз, когда «Учеб» включили. Пока грузится, phase = idle и start не меняется — эффект
     не перезапускается; сняли «Учеб» посреди загрузки — ответ выбрасываем, включат снова — загрузим заново. */
  const start = isLearning && phase === "idle";
  useEffect(() => {
    if (!start) return;
    let live = true;
    Promise.all([
      api<ApiList<LearningStaff>>("/api/bookings/learning-staff"),
      bookingId ? api<ApiList<RawPlayer>>(`/api/bookings/learning?booking_id=${bookingId}`) : Promise.resolve({ results: [] as RawPlayer[] }),
    ]).then(([s, p]) => {
      if (!live) return;
      const cur = (p.results ?? []).map((r) => ({ id: String(r.staff_id), name: r.name || String(r.staff_id) }));
      setStaff(s.results ?? []); setCurrent(cur); setIds(cur.map((r) => r.id)); setPhase("ready");
    }).catch((e: unknown) => {
      if (live) setPhase(e instanceof ApiException && e.status === 403 ? "denied" : "error");
    });
    return () => { live = false; };
  }, [start, bookingId]);

  /* Уволенный, который уже стоит в составе, в кандидатах не придёт — подмешиваем (как снятую спецверсию в модалке),
     иначе одно касание молча выкинуло бы его из истории игры. */
  const pool = useMemo<LearningStaff[]>(() => [
    ...staff,
    ...current.filter((c) => !staff.some((s) => s.id === c.id)).map((c) => ({
      id: c.id, name: c.name, nickname: null, roles: [], has_tg: false, hired_at: null, is_new: false, can_report: false, gone: true,
    })),
  ], [staff, current]);

  /** Выбор пользователя: новый состав + счётчик хосту (кол-во игроков = сколько сотрудников играет).
   *  Тот же набор (Dropdown мультивыбора коммитит и на «тап мимо» — открыл-закрыл) — молчим: иначе кол-во
   *  игроков, набранное руками, сбрасывалось бы, а лид становился бронью от одного взгляда в список. */
  const idsRef = useRef(ids);
  idsRef.current = ids;
  const set = useCallback((next: string[]) => {
    const prev = idsRef.current;
    if (next.length === prev.length && next.every((id) => prev.includes(id))) return;
    idsRef.current = next; setIds(next); pickRef.current?.(next.length);
  }, []);
  const retry = useCallback(() => setPhase("idle"), []);

  return {
    hidden: phase === "denied",
    loading: isLearning && phase === "idle",
    failed: phase === "error",
    pool, ids, set, retry,
    payload: phase === "ready" ? ids : undefined,
    /** Не пусто — выбрано больше, чем примет сервер: текст ошибки для формы. */
    over: ids.length > LEARNING_MAX ? `В учебной игре не больше ${LEARNING_MAX} игроков` : "",
  };
}

export type LearningPlayers = ReturnType<typeof useLearningPlayers>;
